import '@/shared/vendor/GoogleMaps';

import { type ComponentType, forwardRef, type Ref, Suspense, useEffect } from 'react';
// TODO(types): react-dnd 2.6 doesn't have any TypeScript types available.
// Version 3.0 onward does, and I think many of those early releases should be
// backwards-compatible, but I don't have time right now to change and
// verify that.
// @ts-ignore
import { DragDropContext } from 'react-dnd';
import { Route, useHistory } from 'react-router-dom';

import type Account from '@/data/account/model';
import { getAccountPlan } from '@/data/account/selectors';
import { useLDFlag } from '@/data/LD/selectors/getLDFlag';
import type Plan from '@/data/plan/model';
import type User from '@/data/user/model';
import type { UserRole } from '@/data/user/model';
import { openDialog } from '@/dialogs';
import Dialogs from '@/dialogs/containers/DialogContainer';
import Notices from '@/notices/components/list';
import useNotice from '@/notices/hooks/useNotice';
import RipplingMigrationBanner from '@/rippling/components/RipplingMigrationBanner';
import { getStepUpTokenInfo } from '@/shared/auth/auth';
import { STEP_UP_AUTH_DIALOG } from '@/shared/auth/dialogs';
import { getAuthAccount, getAuthData, getAuthUser } from '@/shared/auth/selectors';
import Footer from '@/shared/components/Footer';
import NotAllowed from '@/shared/components/NotAllowed';
import { MainNav } from '@/shared/components/nav';
import SupportButton from '@/shared/components/SupportButton';
import DevPanel from '@/shared/dev/DevPanel';
import { useTimezoneSwitcherCleanup } from '@/shared/hooks/useTimezoneSwitcherCleanup';
import { useLayout } from '@/shared/layouts/context/LayoutContext';
import AddPhoneNumberGate from '@/shared/ui/AddPhoneNumberGate';
import BrowserGate from '@/shared/ui/BrowserGate';
import ErrorBoundary from '@/shared/ui/ErrorBoundary';
import Overlay from '@/shared/ui/loaders/Overlay';
import TermsOfServiceGate from '@/shared/ui/TermsOfServiceGate';
import VerifyAccountLocationGate from '@/shared/ui/VerifyAccountLocationGate';
import DndBackend from '@/shared/util/DndBackend';
import { useWiwDispatch, useWiwSelector } from '@/store';

import classNames from 'classnames';

export interface Props {
  component: ComponentType<any>;
  hasRole?: UserRole[];
  hasAccountCriteria?: (account: Account) => boolean;
  hasAccountPlanCriteria?: (accountPlan: Plan) => boolean;
  hasUserCriteria?: (user: User) => boolean;
  hasFlag?: string;
  elseRedirect?: string | ((user: User, account: Account, accountPlan: Plan) => string);
  hideFooter?: boolean;
  hideNav?: boolean;
  requiresStepUpAuth?: boolean;
  hideRipplingMigrationBanner?: boolean;
}

const defaultProps = {};

export function MainLayout(props: Props, ref: Ref<any>) {
  props = { ...defaultProps, ...props };

  const { hideNav, showFooter, footerVisible, headerVisible } = useLayout();
  const dispatch = useWiwDispatch();
  const auth = useWiwSelector(getAuthData);
  const account = useWiwSelector(getAuthAccount);
  const accountPlan = useWiwSelector(getAccountPlan);
  const history = useHistory();
  const user = useWiwSelector(getAuthUser);
  const LDFlags = useWiwSelector(state => state.LD);
  const pay536StepUpAuthentication = useLDFlag('pay-536-step-up-authentication');
  const { warningNotice } = useNotice();

  useTimezoneSwitcherCleanup();

  useEffect(() => {
    if (props.hideNav) {
      hideNav?.();
    }
    if (!props.hideFooter) {
      showFooter?.();
    }
  }, [props.hideNav, props.hideFooter, showFooter, hideNav]);

  useEffect(() => {
    if (auth.isStepUpAuthIdle && props.requiresStepUpAuth) {
      warningNotice('Your authorization expired while accessing sensitive information.', { duration: 0 });
      history.push('/');
    }
  }, [auth.isStepUpAuthIdle, props.requiresStepUpAuth]);

  const userHasRole = (role: UserRole[] | undefined) => {
    return !(role && !auth.user!.can(role));
  };

  const userHasFlag = (flag: string | undefined) => {
    return !(flag && LDFlags.isLDReady && LDFlags[flag] !== true);
  };

  const checkAccountCriteria = () => {
    const { hasAccountCriteria } = props;
    if (!hasAccountCriteria) {
      return true;
    }

    return hasAccountCriteria(account);
  };

  const checkAccountPlanCriteria = () => {
    const { hasAccountPlanCriteria } = props;
    if (!hasAccountPlanCriteria) {
      return false;
    }

    return hasAccountPlanCriteria(accountPlan);
  };

  const checkUserCriteria = () => {
    const { hasUserCriteria } = props;
    if (!hasUserCriteria) {
      return true;
    }

    return hasUserCriteria(user);
  };

  const stepUpAuthConfirmed = () => {
    const { component: Component, elseRedirect: _, ...rest } = props;
    return <Route ref={ref} {...rest} render={props => <Component {...props} />} />;
  };

  const checkRequiresStepUpAuth = () => {
    const { component: Component, elseRedirect, ...rest } = props;

    const stepUpTokenInfo = getStepUpTokenInfo();
    // Only show dialog if the token is inValid and you are not idle
    // If you were idle, then no reason to show the dialog. This happens on a redirect when user timesout
    if (!stepUpTokenInfo?.isValidStepUpToken() && !auth.isStepUpAuthIdle) {
      let redirect: string | undefined;
      if (elseRedirect && typeof elseRedirect !== 'string') {
        redirect = elseRedirect(user, account, accountPlan);
      } else {
        redirect = elseRedirect;
      }

      dispatch(
        openDialog(STEP_UP_AUTH_DIALOG, {
          successCallback: () => stepUpAuthConfirmed(),
          onCancelRedirect: redirect,
        }),
      );
      return false;
    }
    return <Route ref={ref} {...rest} render={props => <Component {...props} />} />;
  };

  const renderBody = () => {
    const { component: Component, hasRole, hasFlag, elseRedirect, requiresStepUpAuth, ...rest } = props;

    // we want the plan to override the account criteria
    let meetsAccountCriteria = false;
    if (checkAccountPlanCriteria()) {
      meetsAccountCriteria = true;
    } else if (checkAccountCriteria()) {
      meetsAccountCriteria = true;
    }

    if (!userHasRole(hasRole) || !userHasFlag(hasFlag) || !checkUserCriteria() || !meetsAccountCriteria) {
      if (elseRedirect) {
        let redirect: string;
        if (typeof elseRedirect !== 'string') {
          redirect = elseRedirect(user, account, accountPlan);
        } else {
          redirect = elseRedirect;
        }

        window.location.assign(redirect);
        return false;
      }

      return <NotAllowed />;
    }

    if (pay536StepUpAuthentication && requiresStepUpAuth) {
      return checkRequiresStepUpAuth();
    }

    return <Route ref={ref} {...rest} render={props => <Component {...props} />} />;
  };

  return (
    <ErrorBoundary>
      <BrowserGate>
        <div id="wheniwork-is-awesome">
          <TermsOfServiceGate>
            <AddPhoneNumberGate>
              <VerifyAccountLocationGate>
                <div id="content" className="content">
                  <a href="#main-content" className="sr-only">
                    Skip to main content
                  </a>
                  {headerVisible && <MainNav />}
                  {!props.hideRipplingMigrationBanner && <RipplingMigrationBanner />}
                  <div className="container-fluid notice-container px-0">
                    <div className="row no-gutters">
                      <div className="col">
                        <Notices maxVisible={5} followWindow />
                      </div>
                    </div>
                  </div>
                  <div className={classNames('content--inside', { 'no-nav': !headerVisible })} id="main-content">
                    <Suspense fallback={<Overlay />}>{renderBody()}</Suspense>
                    {footerVisible && <Footer />}
                  </div>
                </div>
                <SupportButton />
              </VerifyAccountLocationGate>
            </AddPhoneNumberGate>
          </TermsOfServiceGate>
          <Dialogs />
          <DevPanel />
        </div>
      </BrowserGate>
    </ErrorBoundary>
  );
}

export default DragDropContext(DndBackend)(forwardRef(MainLayout));
